import socketService from '../service/service'
import {
  GET_ALL_USERS_LIST,
  GET_USER_MESSAGES,
  ADD_USER_MESSAGE,
  ADD_ADMIN_MESSAGE,
  UPDATE_MESSAGE_STATUS,
  SET_SOCKET_CONNECTION_STATUS,
} from '../../types'

// Enhanced admin message listener for real-time updates
export function adminMessageListener(dispatch, user) {
  const socket = socketService.getSocket()
  if (!socket) return // safety check

  // Listen for admin message list updates
  socket.on(
    'admin_message_list',
    ({ groupId, data, success, totalUsers, timestamp }) => {
      const userGroupId = user?.groupId
      const matchesGroup =
        groupId == null || userGroupId == null
          ? false
          : String(userGroupId) === String(groupId)

      if (matchesGroup) {
        if (success) {
          dispatch({
            type: GET_ALL_USERS_LIST,
            payload: {
              data,
              total_count: totalUsers,
              lastUpdated: timestamp || new Date().toISOString(),
            },
          })
        } else {
          console.error('❌ Failed to fetch admin message list:', data)
        }
      }
    },
  )

  // Listen for new admin messages
  socket.on('new_admin_message', ({ message, userId, groupId, timestamp }) => {
    const userGroupId = user?.groupId
    const matchesGroup =
      groupId == null || userGroupId == null
        ? false
        : String(userGroupId) === String(groupId)

    if (matchesGroup) {
      dispatch({
        type: ADD_ADMIN_MESSAGE,
        payload: {
          ...message,
          timestamp: timestamp || new Date().toISOString(),
          isNew: true,
        },
      })
    }
  })

  // Listen for message status updates
  socket.on(
    'message_status_update',
    ({ messageId, status, userId, timestamp }) => {
      if (user?.userId === userId) {
        dispatch({
          type: UPDATE_MESSAGE_STATUS,
          payload: {
            messageId,
            status,
            timestamp: timestamp || new Date().toISOString(),
          },
        })
      }
    },
  )
}

// Enhanced message listener for real-time user messages
export function messageListener(dispatch, user) {
  const socket = socketService.getSocket()
  if (!socket) return // safety check

  // Listen for general message updates
  socket.on(
    'messages',
    ({ groupId, data, success, userId, type, timestamp }) => {
      const userGroupId = user?.groupId
      const matchesGroup =
        groupId == null || userGroupId == null
          ? false
          : String(userGroupId) === String(groupId)
      const matchesUser =
        user?.userId != null && String(user.userId) === String(userId)

      if (matchesGroup || matchesUser) {
        if (success) {
          dispatch({
            type: GET_USER_MESSAGES,
            payload: {
              data,
              timestamp: timestamp || new Date().toISOString(),
              source: 'socket',
            },
          })
        } else {
          console.error('❌ Failed to fetch user messages:', data)
        }
      }
    },
  )

  // Listen for new user messages
  socket.on('new_user_message', ({ message, userId, groupId, timestamp }) => {
    const userGroupId = user?.groupId
    const matchesGroup =
      groupId == null || userGroupId == null
        ? false
        : String(userGroupId) === String(groupId)
    const matchesUser =
      user?.userId != null && String(user.userId) === String(userId)

    if (matchesGroup || matchesUser) {
      dispatch({
        type: ADD_USER_MESSAGE,
        payload: {
          ...message,
          timestamp: timestamp || new Date().toISOString(),
          isNew: true,
        },
      })
    }
  })

  // Listen for message read receipts
  socket.on('message_read_receipt', ({ messageId, readBy, readAt, userId }) => {
    if (user?.userId === userId) {
      dispatch({
        type: UPDATE_MESSAGE_STATUS,
        payload: {
          messageId,
          readBy,
          readAt,
          timestamp: new Date().toISOString(),
        },
      })
    }
  })

  // Listen for typing indicators
  socket.on('user_typing', ({ userId, groupId, isTyping }) => {
    const userGroupId = user?.groupId
    const matchesGroup =
      groupId == null || userGroupId == null
        ? false
        : String(userGroupId) === String(groupId)

    if (matchesGroup && userId !== user?.userId) {
      // Dispatch typing indicator action
      dispatch({
        type: 'SET_TYPING_INDICATOR',
        payload: { userId, isTyping, groupId },
      })
    }
  })

  // Listen for user online/offline status
  socket.on('user_status_change', ({ userId, status, lastSeen, groupId }) => {
    const userGroupId = user?.groupId
    const matchesGroup =
      groupId == null || userGroupId == null
        ? false
        : String(userGroupId) === String(groupId)

    if (matchesGroup) {
      dispatch({
        type: 'UPDATE_USER_STATUS',
        payload: { userId, status, lastSeen, groupId },
      })
    }
  })
}

// Enhanced socket connection management
export function socketConnectionListener(dispatch) {
  const socket = socketService.getSocket()
  if (!socket) return

  socket.on('connect', () => {
    console.log('✅ Socket connected:', socket.id)
    dispatch({
      type: SET_SOCKET_CONNECTION_STATUS,
      payload: { connected: true, socketId: socket.id },
    })
  })

  socket.on('disconnect', (reason) => {
    console.log('❌ Socket disconnected:', reason)
    dispatch({
      type: SET_SOCKET_CONNECTION_STATUS,
      payload: { connected: false, reason },
    })
  })

  socket.on('connect_error', (error) => {
    console.error('❌ Socket connection error:', error)
    dispatch({
      type: SET_SOCKET_CONNECTION_STATUS,
      payload: { connected: false, error: error.message },
    })
  })

  socket.on('reconnect', (attemptNumber) => {
    console.log('🔄 Socket reconnected after', attemptNumber, 'attempts')
    dispatch({
      type: SET_SOCKET_CONNECTION_STATUS,
      payload: { connected: true, reconnected: true, attemptNumber },
    })
  })
}

// Enhanced message emission functions
export function emitUserTyping(userId, groupId, isTyping) {
  const socket = socketService.getSocket()
  if (socket) {
    socket.emit('user_typing', { userId, groupId, isTyping })
  }
}

export function emitMessageRead(messageId, userId, groupId) {
  const socket = socketService.getSocket()
  if (socket) {
    socket.emit('message_read', {
      messageId,
      userId,
      groupId,
      readAt: new Date().toISOString(),
    })
  }
}

export function emitUserStatus(userId, status, groupId) {
  const socket = socketService.getSocket()
  if (socket) {
    socket.emit('user_status', {
      userId,
      status,
      groupId,
      timestamp: new Date().toISOString(),
    })
  }
}

// Cleanup function to remove all listeners
export function cleanupMessageListeners() {
  const socket = socketService.getSocket()
  if (socket) {
    socket.off('admin_message_list')
    socket.off('new_admin_message')
    socket.off('message_status_update')
    socket.off('messages')
    socket.off('new_user_message')
    socket.off('message_read_receipt')
    socket.off('user_typing')
    socket.off('user_status_change')
    socket.off('connect')
    socket.off('disconnect')
    socket.off('connect_error')
    socket.off('reconnect')
  }
}
